import { AutocompleteProps, createFilterOptions, FilterOptionsState, PaperProps } from '@mui/material'
import { useMemo, useCallback } from 'react'
import { AssigneeEntity } from '@omnea-app/core/blocks'
import { PlusOMIcon } from '@omnea-app/icons'
import { AutocompleteAction, AutocompleteActionsPaper, BaseAutocomplete } from '@omnea-app/omui/BaseAutocomplete'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { getLabelForUser } from '@omnea-app/packages/universal/users/getLabelForUser'
import { AssigneeAutocompleteGroup } from './AssigneeAutocompleteGroup'
import { AssigneeAutocompleteOption } from './AssigneeAutocompleteOption'

export interface Props<Multiple extends boolean> extends Pick<
  AutocompleteProps<AssigneeEntity, Multiple, undefined, boolean>,
  'multiple' | 'renderInput' | 'open' | 'value' | 'fullWidth' | 'onChange' | 'renderTags' | 'disablePortal' | 'noOptionsText'
> {
  options: AssigneeEntity[]
  disabled?: boolean
  /** MUI anchors the popper at `zIndex.modal` — the same layer as a Dialog, so it renders behind one. */
  elevatePopper?: boolean
  setIsOpen: (isOpen: boolean) => void
  groupBy?: string
  hideGroups?: boolean
  onAddNew?: () => void
  addNewText?: string
  hideUuidInLabel?: boolean
  loading?: boolean
  inputValue?: string
  onInputChange?: (value: string) => void
  userServerSideFilter?: boolean
}

export const getAssigneeLabel = (option: string | AssigneeEntity) => {
  if (typeof option === 'string') {
    return option
  }

  return getLabelForUser({
    name: option.name ?? option.value,
    email: option.email,
  })
}

export const BaseAssigneeAutocomplete = <Multiple extends boolean>({
  options,
  disabled,
  setIsOpen,
  groupBy,
  onAddNew,
  addNewText,
  hideGroups = false,
  hideUuidInLabel = false,
  loading = false,
  inputValue,
  onInputChange,
  userServerSideFilter = false,
  elevatePopper = false,
  ...props
}: Props<Multiple>) => {
  const handleGroupBy = useCallback(
    (option: AssigneeEntity) => (groupBy ? (option as Record<string, any>)[groupBy] : option.type),
    [groupBy]
  )

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => a.type.localeCompare(b.type))
  }, [options])

  const filterOptions = useMemo(() => {
    const clientSideFilter = createFilterOptions({
      matchFrom: 'any',
      stringify: (option: AssigneeEntity) => `${option.value} ${option.name ?? ''} ${option.email ?? ''} ${option.id}`,
    })

    return (options: AssigneeEntity[], state: FilterOptionsState<AssigneeEntity>) => {
      return options.filter((option) => {
        if (option.type === AssigneeTypeEnum.USER && userServerSideFilter) {
          // Already filtered by server, include as-is
          return true
        }

        // Apply client-side filtering to other types
        return clientSideFilter([option], state).length > 0
      })
    }
  }, [userServerSideFilter])

  // Not ideal to do this during render since we potentiallly lose identity, but typings for `slots` and `slotProps` makes
  // shuffling additional props in difficult.
  const addNewActions = useMemo<AutocompleteAction[]>(
    () => (onAddNew && addNewText ? [{ label: addNewText, icon: PlusOMIcon, onSelect: onAddNew }] : []),
    [onAddNew, addNewText]
  )

  const PaperComponent = useCallback(
    (paperProps: PaperProps) => <AutocompleteActionsPaper actions={addNewActions} setIsOpen={setIsOpen} {...paperProps} />,
    [addNewActions, setIsOpen]
  )

  return (
    <BaseAutocomplete
      options={sortedOptions}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      groupBy={hideGroups ? undefined : handleGroupBy}
      renderGroup={(params) => <AssigneeAutocompleteGroup {...params} key={params.key} />}
      sx={{
        ...(disabled && {
          pointerEvents: 'none',
          backgroundColor: 'bg.surface.subtle',
        }),
      }}
      disabled={disabled}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      autoComplete
      getOptionLabel={getAssigneeLabel}
      filterOptions={filterOptions}
      renderOption={(props, option) => (
        <AssigneeAutocompleteOption {...props} hideChip={hideUuidInLabel && isUuid(option.id)} option={option} key={option.id} />
      )}
      filterSelectedOptions
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        // Ignore 'reset' events - MUI fires these when value array reference changes.
        // `clear` is the reason for programmatically resetting the input.
        if (reason === 'reset') {
          return
        }

        onInputChange?.(newValue)
      }}
      slots={{ paper: PaperComponent }}
      slotProps={elevatePopper ? { popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 10 } } } : undefined}
      {...props}
    />
  )
}
